"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import ModalLoadingOverlay from "./ModalLoadingOverlay";
import { getApiErrorMessage } from "../config/toastHelpers";
import { validateForm, type FormErrors } from "../services/validation";
import { userService } from "../services/services";
import { authStorage } from "../lib/authStorage";
import type { UserDetails } from "../types/domain";
import {
  getUserDisplayName,
  getUserPhone,
  getUserPincode,
  getUserAddress,
  getUserCity,
  getUserState,
  buildNamePatch,
  buildAddressPatch,
  buildCityPatch,
  buildStatePatch,
  buildPincodePatch,
  buildPhonePatch,
} from "../lib/userDetails";
import type { BuyDraftPayload } from "../features/trade/types";
import { ModalFrame } from "../features/trade/components/ModalFrame";
import { useCurrentUser } from "../hooks/useCurrentUser";
import BuyModalForm from "./BuyModalForm";

type BuyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  buyDraft: BuyDraftPayload | null;
  onBack?: () => void;
};

const initialForm = {
  name: "",
  street: "",
  apartment: "",
  city: "",
  state: "",
  pincode: "",
  phone: "",
};

type BuyFormErrors = FormErrors;

export default function BuyModal({
  isOpen,
  onClose,
  buyDraft,
  onBack,
}: BuyModalProps) {
  const t = useTranslations("modals");
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState<BuyFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { userDetails, loading: loadingUser } = useCurrentUser();
  const syncUserDetails = (patch: Partial<UserDetails>) => {
    authStorage.mergeUserDetails(patch);
  };

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSubmitting(false);
      setSubmitError("");
      setErrors({});
      if (userDetails) {
        setForm(
          {
            name: buyDraft?.fullName || getUserDisplayName(userDetails) || "",
            street: buyDraft?.address || getUserAddress(userDetails) || "",
            apartment: "",
            city: getUserCity(userDetails) || "",
            state: getUserState(userDetails) || "",
            pincode: buyDraft?.pincode || getUserPincode(userDetails) || "",
            phone: getUserPhone(userDetails) || "",
          });
      }
      return;
    }
    setForm(initialForm);
    setErrors({});
    setSubmitting(false);
    setSubmitError("");
  }, [isOpen, userDetails, buyDraft?.fullName]);

  if (!isOpen) return null;

  const handleSampleSubmit = async () => {
    setSubmitError("");

    const validationErrors = validateForm(form, t);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    if (!buyDraft) {
      setSubmitError("Missing product details. Please try again.");
      return;
    }

    if (!authStorage.hasSession()) {
      setSubmitError("You are not logged in. Please login and try again.");
      return;
    }

    setSubmitting(true);
    try {
      await userService.createSample({
        productId: buyDraft.productId,
        operation: buyDraft.operation,
        recipientName: form.name,
        streetAddress: form.street,
        apartment: form.apartment || undefined,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        phoneNumber: form.phone,
        senderName: "Agarmart",
        fullName: form.name,
      });

      setErrors({});
      setForm(initialForm);
      onClose();
    } catch (error: unknown) {
      setSubmitError(
        getApiErrorMessage(
          error,
          "Failed to request sample. Please try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalFrame>
      {(submitting || loadingUser) && (
        <ModalLoadingOverlay
          message={loadingUser ? t("loadingDetails") : t("submittingRequest")}
        />
      )}

      <div className="relative">
        <div className="sticky top-0 z-20 bg-white px-4 pt-6 pb-4 sm:px-6">
          <button
            onClick={() => {
              if (!submitting && !loadingUser) onClose();
            }}
            disabled={submitting || loadingUser}
            aria-label="Close"
            className="absolute right-4 top-4 z-10 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <div className="w-full px-4 py-6 sm:px-6">
            <div className="mb-6 text-center">
              <h2 className="mb-2 text-xl font-extrabold text-slate-900 sm:text-2xl">
                {t("buy.sampleDeliveryTitle")}
              </h2>
              <p className="text-sm text-slate-500">
                {t("buy.sampleDeliveryDesc")}
              </p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-6 sm:px-6">
          <BuyModalForm
            form={form}
            setForm={setForm}
            syncUserDetails={syncUserDetails}
            buildNamePatch={buildNamePatch}
            buildAddressPatch={buildAddressPatch}
            buildCityPatch={buildCityPatch}
            buildStatePatch={buildStatePatch}
            buildPincodePatch={buildPincodePatch}
            buildPhonePatch={buildPhonePatch}
            errors={errors}
            setErrors={setErrors}
            handleSampleSubmit={handleSampleSubmit}
            submitting={submitting}
            loadingUser={loadingUser}
            submitError={submitError}
            setSubmitError={setSubmitError}
            onBack={onBack}
            onClose={onClose}
          />
        </div>
      </div>
    </ModalFrame>
  );
}
